import type { RAGCitation } from '@/services/api';
import { libraryDocumentPath } from '@/constants/appRoutes';

/** App-relative URL for a document detail page (respects Vite BASE_URL). */
export function getDocumentHref(documentId: string): string {
  const base = import.meta.env.BASE_URL ?? '/';
  const path = libraryDocumentPath(documentId).replace(/^\//, '');
  return base.endsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

const FENCE_SPLIT = /(```[\s\S]*?```)/g;
const PLACEHOLDER_PREFIX = '<<CITE__';
const PLACEHOLDER_SUFFIX = '__>>';

function transformOutsideCodeFences(markdown: string, transform: (s: string) => string): string {
  const parts = markdown.split(FENCE_SPLIT);
  return parts.map((part, i) => (i % 2 === 1 ? part : transform(part))).join('');
}

/**
 * Turn inline citation markers like **[1]** or [1] into links to `/library/documents/:id`
 * when a matching `citations` entry exists. Skips fenced code blocks.
 */
export function linkifyCitationMarkdown(
  markdown: string,
  citations: RAGCitation[] | null | undefined
): string {
  if (!citations?.length) return markdown;
  return transformOutsideCodeFences(markdown, (segment) => linkifySegment(segment, citations));
}

function linkifySegment(segment: string, citations: RAGCitation[]): string {
  const byIndex = new Map<number, string>();
  for (const citation of citations) {
    if (citation.document_id) byIndex.set(citation.index, citation.document_id);
  }

  const indices = [...byIndex.keys()].sort((a, b) => b - a);
  if (indices.length === 0) return segment;

  let output = segment;
  for (const n of indices) {
    const boldRe = new RegExp(`\\*\\*\\s*\\[${n}\\]\\s*\\*\\*`, 'g');
    output = output.replace(
      boldRe,
      `${PLACEHOLDER_PREFIX}BOLD_${n}${PLACEHOLDER_SUFFIX}`
    );
  }
  for (const n of indices) {
    const plainRe = new RegExp(`\\[${n}\\](?!\\()`, 'g');
    output = output.replace(
      plainRe,
      `${PLACEHOLDER_PREFIX}PLAIN_${n}${PLACEHOLDER_SUFFIX}`
    );
  }

  for (const n of indices) {
    const docId = byIndex.get(n);
    if (!docId) continue;
    const href = getDocumentHref(docId);
    output = output
      .split(`${PLACEHOLDER_PREFIX}BOLD_${n}${PLACEHOLDER_SUFFIX}`)
      .join(`<a href="${href}" target="_blank" rel="noopener noreferrer"><strong>[${n}]</strong></a>`);
    output = output
      .split(`${PLACEHOLDER_PREFIX}PLAIN_${n}${PLACEHOLDER_SUFFIX}`)
      .join(`<a href="${href}" target="_blank" rel="noopener noreferrer">[${n}]</a>`);
  }

  return output;
}
